import React, { useState, useEffect } from "react";
import {
  message,
  Modal,
  Table,
  Button,
  Form,
  Input,
  Space,
  Upload,
  Switch,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { IndoorPositioningPersonnel } from "../../api";
import {
  DUTY_SWITCH_LABELS,
  DUTY_TOGGLE_TIMEOUT_MS,
  dutyChangeMessage,
  isOnDuty,
  setOnDutyById,
} from "./personnelDuty";
import {
  canonicalDeviceId,
  deviceIdRule,
  findDeviceIdColumn,
  isValidDeviceId,
} from "../../utils/deviceId";

const PersonnelModal = ({ open, onClose, machineID }) => {
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  // ids whose duty toggle is mid-flight; keeps the row-level switch busy
  // without freezing the whole table, and swallows repeat clicks so two
  // updates for one person can never land out of order.
  const [dutyPendingIds, setDutyPendingIds] = useState([]);

  const totalCount = personnel.length;
  const onDutyCount = personnel.filter(isOnDuty).length;

  const loadPersonnel = async () => {
    setLoading(true);
    try {
      const response = await IndoorPositioningPersonnel.GetPersonnel(machineID);
      if (response.code === 200) {
        const list = (response.data || []).map((item) => ({
          ...item,
          onDuty: item.onDuty ?? item.on_duty ?? false,
        }));
        setPersonnel(list);
      } else {
        message.error(response.message || "獲取人員列表失敗");
      }
    } catch (error) {
      console.error("載入人員資料失敗:", error);
      message.error("載入人員資料失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPersonnel();
    }
  }, [open, machineID]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ onDuty: false });
    setEditModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: `確定要刪除人員「${record.name}」嗎？`,
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await IndoorPositioningPersonnel.DeletePersonnel(
            record.id,
          );
          if (response.code === 200) {
            message.success("刪除人員成功");
            loadPersonnel();
          } else {
            message.error(response.message || "刪除人員失敗");
          }
        } catch (error) {
          console.error("刪除人員失敗:", error);
          message.error("刪除人員失敗");
        }
      },
    });
  };

  /**
   * Flip 是否為值班人員 straight from the list row.
   *
   * Updates optimistically and rolls back on failure rather than refetching:
   * a full reload flashes the table spinner and drops scroll position, which
   * is exactly wrong when an operator is toggling several people in a row.
   * The header counters read from `personnel`, so they follow along for free.
   * Only `onDuty` is sent -- the PUT endpoint is a partial update, so nothing
   * else on the record is touched and device ID validation is not re-run.
   */
  const handleToggleDuty = async (record, nextOnDuty) => {
    if (dutyPendingIds.includes(record.id)) {
      return;
    }
    const previous = personnel;
    setDutyPendingIds((ids) => [...ids, record.id]);
    setPersonnel((list) => setOnDutyById(list, record.id, nextOnDuty));

    try {
      const response = await IndoorPositioningPersonnel.UpdatePersonnel(
        record.id,
        { onDuty: nextOnDuty },
        { timeout: DUTY_TOGGLE_TIMEOUT_MS },
      );
      if (response.code === 200) {
        message.success(dutyChangeMessage(record.name, nextOnDuty));
      } else {
        setPersonnel(previous);
        message.error(response.message || "更新值班狀態失敗");
      }
    } catch (error) {
      console.error("更新值班狀態失敗:", error);
      setPersonnel(previous);
      message.error("更新值班狀態失敗");
    } finally {
      setDutyPendingIds((ids) => ids.filter((id) => id !== record.id));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let response;

      if (editingRecord) {
        response = await IndoorPositioningPersonnel.UpdatePersonnel(
          editingRecord.id,
          {
            ...values,
            device_id: canonicalDeviceId(values.device_id),
            indoor_positioning_id: machineID,
            on_duty: values.onDuty,
          },
        );
      } else {
        response = await IndoorPositioningPersonnel.CreatePersonnel({
          ...values,
          device_id: canonicalDeviceId(values.device_id),
          indoor_positioning_id: machineID,
          on_duty: values.onDuty,
        });
      }

      if (response.code === 200) {
        message.success(editingRecord ? "更新人員成功" : "新增人員成功");
        setEditModalVisible(false);
        form.resetFields();
        setEditingRecord(null);
        loadPersonnel();
      } else {
        message.error(
          response.message || (editingRecord ? "更新人員失敗" : "新增人員失敗"),
        );
      }
    } catch (error) {
      console.error("提交人員失敗:", error);
      if (error.errorFields) {
        return;
      }
      message.error(editingRecord ? "更新人員失敗" : "新增人員失敗");
    }
  };

  const handleBatchImport = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        const headers = lines[0].split(",").map((h) => h.trim());

        const expectedHeaders = [
          "名稱",
          "公司",
          "職稱",
          "血型",
          "是否為值班人員",
        ];
        const hasValidHeaders =
          expectedHeaders.every((h) => headers.includes(h)) &&
          findDeviceIdColumn(headers) !== -1;

        if (!hasValidHeaders) {
          message.error(
            "CSV 格式錯誤：標題行應包含「名稱」、「裝置ID」、「公司」、「職稱」、「血型」、「是否為值班人員」",
          );
          return false;
        }

        const nameIdx = headers.indexOf("名稱");
        const deviceIdIdx = findDeviceIdColumn(headers);
        const companyIdx = headers.indexOf("公司");
        const jobTitleIdx = headers.indexOf("職稱");
        const bloodTypeIdx = headers.indexOf("血型");
        const onDutyIdx = headers.indexOf("是否為值班人員");

        const importedPersonnel = [];
        const skippedLines = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(",").map((v) => v.trim());
          if (values.length >= 2 && values[nameIdx] && values[deviceIdIdx]) {
            if (!isValidDeviceId(values[deviceIdIdx])) {
              skippedLines.push(i + 1);
              continue;
            }
            const onDuty =
              values[onDutyIdx] === "是" ||
              values[onDutyIdx] === "true" ||
              values[onDutyIdx] === "1" ||
              values[onDutyIdx] === "True";

            importedPersonnel.push({
              name: values[nameIdx],
              device_id: canonicalDeviceId(values[deviceIdIdx]),
              company: values[companyIdx] || "",
              jobTitle: values[jobTitleIdx] || "",
              bloodType: values[bloodTypeIdx] || "",
              onDuty,
              on_duty: onDuty,
            });
          }
        }

        if (skippedLines.length > 0) {
          message.warning(
            `已略過裝置 ID 格式不正確的第 ${skippedLines.join("、")} 行`,
          );
        }

        if (importedPersonnel.length > 0) {
          const response =
            await IndoorPositioningPersonnel.BatchImportPersonnel(
              importedPersonnel,
              machineID,
            );
          if (response.code === 200) {
            message.success(
              response.message ||
                `成功匯入 ${importedPersonnel.length} 筆人員資料`,
            );
            setImportModalVisible(false);
            setFileList([]);
            loadPersonnel();
          } else {
            message.error(response.message || "匯入失敗");
          }
        } else {
          message.warning("沒有有效的資料可匯入");
        }
      } catch (error) {
        console.error("匯入失敗:", error);
        message.error("匯入失敗：" + error.message);
      }
    };
    reader.onerror = () => {
      message.error("讀取檔案失敗");
    };
    reader.readAsText(file);
    return false;
  };

  const handleDownloadExample = () => {
    const csvContent = `名稱,裝置ID,公司,職稱,血型,是否為值班人員
人員1,AABBCCDDEE01,範例公司A,工程師,A+,是
人員2,AABBCCDDEE02,範例公司B,主管,B+,否
人員3,AABBCCDDEE03,範例公司C,技術員,O+,是`;

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "人員匯入範例.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("範例檔案下載成功");
  };

  const columns = [
    {
      title: "名稱",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "裝置 ID",
      dataIndex: "device_id",
      key: "device_id",
      align: "center",
    },
    {
      title: "公司",
      dataIndex: "company",
      key: "company",
      align: "center",
    },
    {
      title: "職稱",
      dataIndex: "jobTitle",
      key: "jobTitle",
      align: "center",
    },
    {
      title: "血型",
      dataIndex: "bloodType",
      key: "bloodType",
      align: "center",
    },
    {
      title: "是否為值班人員",
      dataIndex: "onDuty",
      key: "onDuty",
      align: "center",
      render: (_, record) => (
        <Switch
          {...DUTY_SWITCH_LABELS}
          checked={isOnDuty(record)}
          loading={dutyPendingIds.includes(record.id)}
          disabled={dutyPendingIds.includes(record.id)}
          onChange={(checked) => handleToggleDuty(record, checked)}
        />
      ),
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  const uploadProps = {
    beforeUpload: (file) => {
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        message.error("只能上傳 CSV 檔案");
        return false;
      }
      handleBatchImport(file);
      return false;
    },
    fileList,
    accept: ".csv",
  };

  return (
    <>
      <Modal
        title="人員資料"
        open={open}
        onCancel={onClose}
        footer={null}
        width={900}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="font-weight-bold mr-2">人員總數：</span>
              <span>{totalCount} 人</span>
              <span className="ml-3 font-weight-bold mr-2">值班人員：</span>
              <span>{onDutyCount} 人</span>
            </div>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增人員
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportModalVisible(true)}
              >
                批次匯入人員
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={personnel}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            locale={{
              emptyText: "暫無資料",
            }}
          />
        </div>
      </Modal>

      <Modal
        title={editingRecord ? "編輯人員" : "新增人員"}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setEditingRecord(null);
        }}
        onOk={handleSubmit}
        okText="確認"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="人員名稱"
            name="name"
            rules={[{ required: true, message: "請輸入人員名稱" }]}
          >
            <Input placeholder="請輸入人員名稱" />
          </Form.Item>
          <Form.Item
            label="人員裝置 ID"
            name="device_id"
            rules={[
              { required: true, message: "請輸入人員裝置 ID" },
              deviceIdRule,
            ]}
          >
            <Input placeholder="請輸入人員裝置 ID (例如: AABBCCDDEEFF)" />
          </Form.Item>
          <Form.Item label="公司" name="company">
            <Input placeholder="請輸入公司名稱" />
          </Form.Item>
          <Form.Item label="職稱" name="jobTitle">
            <Input placeholder="請輸入職稱" />
          </Form.Item>
          <Form.Item label="血型" name="bloodType">
            <Select placeholder="請選擇血型" allowClear>
              <Select.Option value="A+">A+</Select.Option>
              <Select.Option value="A-">A-</Select.Option>
              <Select.Option value="B+">B+</Select.Option>
              <Select.Option value="B-">B-</Select.Option>
              <Select.Option value="AB+">AB+</Select.Option>
              <Select.Option value="AB-">AB-</Select.Option>
              <Select.Option value="O+">O+</Select.Option>
              <Select.Option value="O-">O-</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="是否為值班人員"
            name="onDuty"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch {...DUTY_SWITCH_LABELS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批次匯入人員"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setFileList([]);
        }}
        footer={null}
      >
        <div className="p-3">
          <p className="mb-3">請上傳人員 CSV 檔案，格式如下：</p>
          <div
            className="mb-3"
            style={{
              backgroundColor: "#f5f5f5",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <code>
              名稱,裝置ID,公司,職稱,血型,是否為值班人員
              <br />
              人員1,AABBCCDDEE01,範例公司A,工程師,A+,是
              <br />
              人員2,AABBCCDDEE02,範例公司B,主管,B+,否
            </code>
          </div>
          <Space>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>選擇 CSV 檔案</Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadExample}>
              下載範例檔案
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default PersonnelModal;
